import React from "react";
import { Check, Star } from "@phosphor-icons/react";
import PayButton from "@/components/landing/PayButton";

const TIERS = [
  {
    name: "Basic Tune-Up",
    price: "$79",
    freq: "per device",
    tagline: "For a single slow PC or laptop",
    packageId: "basic_tuneup",
    features: [
      "Complete system tune-up",
      "Junk & temp file cleanup",
      "Startup optimization",
      "Driver & OS updates",
      "7-day satisfaction guarantee",
    ],
    featured: false,
  },
  {
    name: "Complete Care",
    price: "$29",
    freq: "/ month",
    tagline: "Our most popular — peace of mind",
    packageId: "complete_care",
    features: [
      "Everything in Basic Tune-Up",
      "Malware & virus removal included",
      "Automated cloud backup (500GB)",
      "Unlimited remote support",
      "Priority same-day response",
      "Quarterly health check-ups",
    ],
    featured: true,
  },
  {
    name: "Business",
    price: "Custom",
    freq: "quote",
    tagline: "For teams of 5 – 500+",
    packageId: null,
    features: [
      "Everything in Complete Care",
      "Managed endpoints & patching",
      "Business-grade backup & DR",
      "On-site service visits",
      "Dedicated account manager",
      "99.9% uptime SLA",
    ],
    featured: false,
  },
];

export default function Pricing({ onBook }) {
  return (
    <section id="pricing" data-testid="pricing-section" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="text-sm font-semibold uppercase tracking-widest text-blue-600">Pricing</div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900">
            Honest pricing. No surprises.
          </h2>
          <p className="mt-4 text-lg text-zinc-600">
            Book an appointment, or pay online in seconds with Stripe. Cards, Apple Pay, and Google Pay accepted.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {TIERS.map((t, i) => (
            <div
              key={t.name}
              data-testid={`pricing-tier-${i}`}
              className={`relative flex flex-col rounded-2xl p-8 ${
                t.featured
                  ? "bg-zinc-950 text-white tracing-beam lg:scale-[1.03] lg:-translate-y-2"
                  : "bg-white border border-zinc-200"
              }`}
            >
              {t.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-blue-600 text-white text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full">
                  <Star size={12} weight="fill" /> Most Popular
                </div>
              )}

              <h3 className={`font-heading text-lg font-semibold ${t.featured ? "text-zinc-300" : "text-zinc-900"}`}>
                {t.name}
              </h3>
              <div className="mt-4 flex items-baseline gap-2">
                <span className={`font-heading text-5xl font-extrabold tracking-tighter ${t.featured ? "text-white" : "text-zinc-900"}`}>
                  {t.price}
                </span>
                <span className={`text-sm ${t.featured ? "text-zinc-400" : "text-zinc-500"}`}>{t.freq}</span>
              </div>
              <p className={`mt-2 text-sm ${t.featured ? "text-zinc-400" : "text-zinc-500"}`}>{t.tagline}</p>

              <ul className="mt-8 space-y-3 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[15px]">
                    <Check size={18} weight="bold" className={t.featured ? "text-blue-400 shrink-0 mt-0.5" : "text-blue-600 shrink-0 mt-0.5"} />
                    <span className={t.featured ? "text-zinc-200" : "text-zinc-700"}>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-col gap-2.5">
                {t.packageId ? (
                  <PayButton
                    packageId={t.packageId}
                    testId={`pricing-pay-${i}`}
                    variant={t.featured ? "primary" : "dark"}
                    className="h-11 px-5 text-sm"
                  >
                    Pay {t.price}{t.freq.includes("month") ? " / mo" : ""} online
                  </PayButton>
                ) : (
                  <button
                    onClick={() => { const el = document.getElementById("contact"); el && el.scrollIntoView({ behavior: "smooth" }); }}
                    data-testid={`pricing-quote-${i}`}
                    className="inline-flex items-center justify-center rounded-full px-5 h-11 text-sm bg-zinc-900 hover:bg-zinc-800 text-white font-semibold transition-all"
                  >
                    Request a quote
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onBook && onBook(t.name)}
                  data-testid={`pricing-book-${i}`}
                  className={`inline-flex items-center justify-center rounded-full px-5 h-11 text-sm font-semibold transition-all ${
                    t.featured
                      ? "bg-white/10 hover:bg-white/20 text-white border border-white/20"
                      : "bg-zinc-100 hover:bg-zinc-200 text-zinc-900 border border-zinc-200"
                  }`}
                >
                  Book appointment
                </button>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-zinc-500">
          Payments processed securely by Stripe. Your card details never touch our servers.
        </p>
      </div>
    </section>
  );
}
